import { useCallback, useEffect, useState } from 'react';
import Drawer from 'react-modern-drawer';
import { FormGroup, Input, Label, Spinner } from 'reactstrap';
import DefaultButton from '../../../../../components/Buttons/Default';
import { get, post } from '../../../../../utils/helpers/api';
import { useTranslation } from 'react-i18next';

function CreateDrawer({ isOpen, toggle, editData, onSaved }) {
  const { t } = useTranslation();
  const [saveLoading, setSaveLoading] = useState(false);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [groupedPermissions, setGroupedPermissions] = useState({});
  const [roleData, setRoleData] = useState({
    name: editData?.name || '',
    permissions: editData?.permissions?.map((p) => p.id) || [],
  });

  const fetchPermissions = useCallback(async () => {
    setPermissionsLoading(true);
    const data = await get('/settings/roles');
    if (data?.permissions) {
      const grouped = data.permissions.reduce((acc, perm) => {
        if (!acc[perm.group]) acc[perm.group] = [];
        acc[perm.group].push(perm);
        return acc;
      }, {});
      setGroupedPermissions(grouped);
    }
    setPermissionsLoading(false);
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchPermissions();
      if (editData) {
        setRoleData({
          name: editData.name,
          permissions: editData.permissions?.map((p) => p.id) ?? [],
        });
      } else {
        setRoleData({ name: '', permissions: [] });
      }
    }
  }, [isOpen, editData, fetchPermissions]);

  const togglePermission = (id) => {
    setRoleData((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(id)
        ? prev.permissions.filter((p) => p !== id)
        : [...prev.permissions, id],
    }));
  };

  const toggleGroup = (perms) => {
    const ids = perms.map((p) => p.id);
    const allSelected = ids.every((id) => roleData.permissions.includes(id));
    setRoleData((prev) => ({
      ...prev,
      permissions: allSelected
        ? prev.permissions.filter((id) => !ids.includes(id))
        : [...new Set([...prev.permissions, ...ids])],
    }));
  };

  const handleSave = async () => {
    setSaveLoading(true);
    const endpoint = editData
      ? `/settings/roles/update/${editData.id}`
      : '/settings/roles/store';
    const result = await post(endpoint, roleData);
    setSaveLoading(false);
    if (result) {
      onSaved?.();
      toggle();
    }
  };

  return (
    <Drawer
      open={isOpen}
      onClose={toggle}
      direction="right"
      size={'50%'}
      enableOverlay={true}
      lockScroll={true}
      zIndex={9000}
      duration={200}
    >
      <div className="p-3 d-flex flex-column h-100">
        <h4 className="fw-bold mb-3">{editData ? t('settings.editRole') : t('settings.newRole')}</h4>

        <FormGroup>
          <Label>{t('common.name')}</Label>
          <Input
            value={roleData.name}
            onChange={(e) => setRoleData({ ...roleData, name: e.target.value })}
          />
        </FormGroup>

        <Label className="fw-semibold mb-2">{t('settings.permission')}</Label>

        {permissionsLoading ? (
          <div className="text-center py-4">
            <Spinner size="sm" />
          </div>
        ) : (
          <div className="overflow-auto flex-grow-1 pe-1">
            {Object.entries(groupedPermissions).map(([group, perms]) => {
              const allSelected = perms.every((p) =>
                roleData.permissions.includes(p.id)
              );
              const someSelected = perms.some((p) =>
                roleData.permissions.includes(p.id)
              );

              return (
                <div key={group} className="mb-3 border rounded p-2">
                  <div
                    className="d-flex align-items-center gap-2 mb-2 cursor-pointer"
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleGroup(perms)}
                  >
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = someSelected && !allSelected;
                      }}
                      onChange={() => toggleGroup(perms)}
                      onClick={(e) => e.stopPropagation()}
                      style={{ cursor: 'pointer' }}
                    />
                    <span className="fw-semibold text-capitalize">{group}</span>
                  </div>

                  <div className="d-flex flex-wrap gap-2 ps-2">
                    {perms.map((perm) => (
                      <FormGroup check key={perm.id} className="mb-0">
                        <Input
                          type="checkbox"
                          id={`perm-${perm.id}`}
                          checked={roleData.permissions.includes(perm.id)}
                          onChange={() => togglePermission(perm.id)}
                        />
                        <Label check for={`perm-${perm.id}`} style={{ cursor: 'pointer' }}>
                          {perm.name_en}
                        </Label>
                      </FormGroup>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-3">
          <DefaultButton onClick={handleSave} disabled={saveLoading}>
            {saveLoading ? <Spinner size="sm" /> : t('common.save')}
          </DefaultButton>
        </div>
      </div>
    </Drawer>
  );
}

export default CreateDrawer;
